/**
 * Builds a detailplaneering rich-editor draft from a parcel hoone's EHR data
 * (RED-268): the ehitusluba sentence, a key/value spec table (renders
 * borderless in the export, like the hand-authored ehitusõigus blocks), and
 * the realiseerimata remark. Pure functions — Tiptap JSON in/out, no I/O.
 */

const EMPTY = ['', '-', null, undefined]

const paragraph = (text, marks = []) => ({
  type: 'paragraph',
  ...(text
    ? { content: [{ type: 'text', text, ...(marks.length ? { marks } : {}) }] }
    : {}),
})

const tableRow = cells => ({
  type: 'tableRow',
  content: cells.map(text => ({
    type: 'tableCell',
    content: [paragraph(text)],
  })),
})

const parseEstonianDate = value => {
  const parts = String(value || '').split('.')
  return parts.length === 3 ? new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() || 0 : 0
}

const newestDocument = (documents, predicate) =>
  documents
    .filter(predicate)
    .sort((a, b) => parseEstonianDate(b['Kuupäev']) - parseEstonianDate(a['Kuupäev']))[0] ?? null

/**
 * @param {Object} input
 * @param {Object} input.building  Hooned ja rajatised row ({ name, status, usage })
 * @param {Object} input.detail   /xroad/ehr/{code} payload ({ general, technical })
 * @param {Array}  input.documents /xroad/documents 'everything' rows ({ Nr, Kuupäev, Nimetus, Seisund })
 * @returns {{ content: Array, shortValue: string }|null} null when EHR has nothing usable
 */
export function buildPlanningEhrDraft({ building, detail, documents }) {
  const general = detail?.general ?? {}
  const technical = detail?.technical ?? {}
  const docs = documents ?? []

  const ehitusluba = newestDocument(
    docs,
    d =>
      String(d.Nimetus || '').startsWith('Ehitusluba') &&
      ['Antud', 'Registrisse kantud', 'Kehtiv'].includes(d.Seisund),
  )
  const projekteerimistingimused = newestDocument(docs, d =>
    String(d.Nimetus || '').includes('rojekteerimistingimused'),
  )
  const kasutusluba = newestDocument(docs, d => String(d.Nimetus || '').startsWith('Kasutusluba'))

  const usage = general['peamine-kasutusotstarve'] || building?.usage || building?.name || ''
  const status = building?.status || general['ehitise-seisund'] || ''

  const specRows = [
    ['Kasutusotstarve', usage],
    ['Ehitisealune pind', technical['ehitisealune-pind'], 'm²'],
    ['Suletud netopind', technical['snp'], 'm²'],
    ['Maapealseid korruseid', technical['maapealseid-korruseid']],
    ['Maa-aluseid korruseid', technical['maa-alused-korrused'] === '0' ? '' : technical['maa-alused-korrused']],
    ['Hoone maht', technical['hoone-maht'], 'm³'],
  ].filter(([, value]) => !EMPTY.includes(value))

  if (!ehitusluba && !projekteerimistingimused && !specRows.length) {
    return null
  }

  const content = []

  const permitSentences = []
  if (ehitusluba) {
    permitSentences.push(`Kinnistule on väljastatud ehitusluba nr ${ehitusluba.Nr} (${ehitusluba['Kuupäev']}).`)
  }
  if (projekteerimistingimused) {
    permitSentences.push(
      `Projekteerimistingimused nr ${projekteerimistingimused.Nr} (${projekteerimistingimused['Kuupäev']}).`,
    )
  }
  if (permitSentences.length) {
    content.push(paragraph(permitSentences.join(' ')))
  }

  if (specRows.length) {
    content.push(paragraph(''))
    content.push(paragraph('Ehitusloa kohaselt kavandatud hoone näitajad:'))
    content.push({
      type: 'table',
      content: specRows.map(([label, value, unit]) =>
        tableRow([`${label}:`, unit ? `${value} ${unit}` : String(value)]),
      ),
    })
  }

  const remark = kasutusluba
    ? 'Hoonele on väljastatud kasutusluba.'
    : status === 'Kavandatav'
      ? 'Ehitusõigus on hindamise hetkel täies ulatuses realiseerimata, krunt on hoonestamata.'
      : status === 'Püstitamisel'
        ? 'Hoone on püstitamisel, ehitusõigus on realiseerimisel.'
        : ''
  if (remark) {
    content.push(paragraph(''))
    content.push(paragraph(remark, [{ type: 'italic' }]))
  }

  content.push(paragraph(''))
  content.push(paragraph('Allikas: Ehitisregister, ehr.ee'))

  const year = ehitusluba ? ehitusluba['Kuupäev']?.slice(-4) : null
  const shortValue = ehitusluba
    ? `Ehitusluba nr ${ehitusluba.Nr} (${year})${kasutusluba ? '' : ', realiseerimata'}`
    : status === 'Kavandatav' && usage
      ? `Kavandatav ${usage.charAt(0).toLowerCase()}${usage.slice(1)}`
      : ''

  return { content, shortValue }
}

/** True when the doc is empty or holds nothing but blank paragraphs. */
export function isBlankPlanningDoc(doc) {
  if (!doc?.content?.length) return true
  return doc.content.every(
    node =>
      node.type === 'paragraph' &&
      !(node.content || []).some(child => child.type === 'text' && child.text.trim() !== ''),
  )
}
